import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import type { Lesson, StrandId, Unit } from '@yuan-math/curriculum';
import { registry, useLearner } from '../state/LearnerContext.tsx';

/**
 * A course, drawn as a path.
 *
 * A course *is* a walk through the graph, and a numbered list of rows hides
 * that. Nodes wander left and right on a fixed eight-step cycle (in CSS, so it
 * behaves the same at every width), each unit gets its own colour, and exactly
 * one node carries a "Start" flag — the first lesson the student has not
 * finished — so the answer to "what do I press?" is never a decision.
 *
 * **Nothing on this path is locked, and that is deliberate.** The obvious way
 * to build a path like this is Duolingo's, where node *n* opens only when
 * *n − 1* is done. This platform refuses that: a concept is not owned by a
 * grade, mathematics is a graph rather than a ladder, and `canOpenLesson()`
 * returns true unconditionally on purpose. A grey node here means "you have
 * not done this yet", never "you may not". A student who wants the last lesson
 * first is allowed to have it — the mastery ring around each node is what
 * tells the truth about how it went.
 */

/**
 * A face for each branch of the subject.
 *
 * Presentational, and therefore here rather than in the curriculum: the graph
 * describes mathematics, not how mathematics is decorated. Every icon is
 * `aria-hidden` and paired with the lesson title, so nothing depends on
 * recognising a glyph.
 */
const STRAND_ICON: Record<StrandId, string> = {
  'early-math': '🔢',
  numbers: '🔟',
  arithmetic: '➕',
  fractions: '🍕',
  decimals: '💰',
  measurement: '📏',
  geometry: '📐',
  algebra: '🅰️',
  functions: '📈',
  trigonometry: '🌊',
  probability: '🎲',
  statistics: '📊',
  discrete: '🧩',
  'number-theory': '🔑',
  calculus: '♾️',
  'linear-algebra': '🧮',
  'differential-equations': '🌀',
  analysis: '🔬',
  'abstract-algebra': '🔷',
  topology: '🕸️',
  competition: '🏆',
};

function iconFor(lesson: Lesson): string {
  const concept = registry.concept(lesson.conceptId);
  return concept ? (STRAND_ICON[concept.strand] ?? '✏️') : '✏️';
}

/** The mastery ring drawn around a node. */
function MasteryRing({ value }: { value: number }): JSX.Element {
  const radius = 43;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.min(1, Math.max(0, value));
  return (
    <svg className="path-node__ring" viewBox="0 0 90 90" aria-hidden="true" focusable="false">
      <circle className="path-node__ring-track" cx="45" cy="45" r={radius} />
      <circle
        className="path-node__ring-fill"
        cx="45"
        cy="45"
        r={radius}
        strokeDasharray={`${circumference * fraction} ${circumference}`}
      />
    </svg>
  );
}

function PathStep({ lesson, isNext, done }: { lesson: Lesson; isNext: boolean; done: boolean }): JSX.Element {
  const { profile, masteryOf } = useLearner();
  const mastery = profile ? masteryOf(lesson.conceptId).overall : 0;

  return (
    <li className={`path-step ${isNext ? 'path-step--next' : ''}`}>
      {isNext ? <span className="path-flag">Start</span> : null}

      <Link
        to={`/lessons/${lesson.id}`}
        className={`path-node ${done ? '' : 'path-node--todo'}`}
        aria-label={`${lesson.title} — ${done ? 'finished' : 'not started'}, ${Math.round(mastery * 100)}% mastery`}
      >
        {profile ? <MasteryRing value={mastery} /> : null}
        <span aria-hidden="true">{iconFor(lesson)}</span>
        {done ? (
          <span className="path-node__tick" aria-hidden="true">
            ✓
          </span>
        ) : null}
      </Link>

      <span className="path-step__title">{lesson.title}</span>
      <span className="path-step__minutes">{lesson.estimatedMinutes} min</span>
      <Link to={`/practice/${lesson.conceptId}`} className="btn btn--ghost btn--sm path-step__practise">
        Practise
      </Link>
    </li>
  );
}

export function PathMap({ units }: { units: Unit[] }): JSX.Element {
  const { profile } = useLearner();
  const completed = new Set(profile?.completedLessonIds ?? []);

  // The one node that gets the flag: the first unfinished lesson in course
  // order. Computed across the whole course rather than per unit, so a student
  // halfway through unit three is not also told to start unit four.
  const nextLessonId = units
    .flatMap((unit) => registry.lessonsOfUnit(unit.id))
    .find((lesson) => !completed.has(lesson.id))?.id;

  return (
    <ol className="path">
      {units.map((unit, unitIndex) => {
        const lessons = registry.lessonsOfUnit(unit.id);
        const done = lessons.filter((lesson) => completed.has(lesson.id)).length;

        return (
          <li key={unit.id} className="path-unit">
            <header className="path-unit__banner">
              <div>
                <p className="path-unit__index">Unit {unitIndex + 1}</p>
                <h2 className="path-unit__title">{unit.title}</h2>
                <p className="path-unit__description">{unit.description}</p>
              </div>
              <span className="path-unit__count">
                {done}/{lessons.length}
              </span>
            </header>

            <ol className="path-lessons">
              {lessons.map((lesson) => (
                <PathStep
                  key={lesson.id}
                  lesson={lesson}
                  done={completed.has(lesson.id)}
                  isNext={lesson.id === nextLessonId}
                />
              ))}
            </ol>
          </li>
        );
      })}

      {nextLessonId === undefined && units.length > 0 ? (
        <li className="path-finish">
          <p className="path-finish__title">Every lesson in this course is done.</p>
          <p className="path-finish__note">
            Mastery keeps moving after the last lesson — review brings these back just as they start to fade.
          </p>
        </li>
      ) : null}
    </ol>
  );
}
